/**
 * useUnsavedChangesGuard — block navigation (router + tab close) while a form
 * is dirty, with a 3-way resolution dialog (Save / Discard / Cancel).
 *
 * Introduced in F3.5 Wave 4 Pass 2b for B15: users who edit a Company
 * Profile form and then navigate away (breadcrumb, sidebar, back button,
 * close tab) were silently losing their work.
 *
 * The hook owns the navigation-blocking side effects; the caller owns the
 * dialog chrome (render `<UnsavedChangesDialog />` wired to the returned
 * handlers). Keeping the dialog out of the hook lets it work for both
 * page-level forms and modal forms.
 *
 * Contract (documented in developer guidelines after this landed):
 *   "Any form persisting data asynchronously MUST use useUnsavedChangesGuard.
 *    QA will reject otherwise." Applied to Company Profile now; the rest of
 *    the app is swept in a later milestone.
 *
 * Inputs:
 *   - `isDirty` — parent computes this (comparing form state to the server
 *     snapshot). When true, the guard is armed.
 *   - `onSave` — called when the user picks "Save". Can be sync or async;
 *     the hook resolves the block only after the promise settles.
 *   - `onDiscard` — optional. Called when the user picks "Discard" so the
 *     parent can reset its local state before the blocked navigation
 *     resumes. If omitted, the navigation simply proceeds.
 *
 * Why two layers of blocking:
 *   - `useBlocker` covers in-app navigation (RR6 Data Router). It's the
 *     primary UX path — the user clicks a sidebar link, we show the dialog.
 *   - `beforeunload` covers tab close / reload / external-URL navigation.
 *     The browser shows its native "Leave site?" prompt; we can't customise
 *     it, but at least the user isn't silently losing work.
 */

import { useCallback, useEffect, useState } from 'react';
import { useBlocker, type Location } from 'react-router-dom';

export interface UseUnsavedChangesGuardOptions {
  /** True when the form has unsaved edits. */
  isDirty: boolean;
  /** Called when the user picks "Save". May return a Promise. */
  onSave: () => Promise<void> | void;
  /** Optional: called when the user picks "Discard" (to reset form state). */
  onDiscard?: () => void;
}

export interface UseUnsavedChangesGuardReturn {
  /** True when a blocked navigation is pending and the dialog should show. */
  isBlocked: boolean;
  /** Run onSave, then release the block and proceed. */
  save: () => Promise<void>;
  /** Run onDiscard, then release the block and proceed. */
  discard: () => void;
  /** Stay on the current page (dismiss the dialog, keep the form state). */
  cancel: () => void;
  /** True while onSave is in flight; buttons should be disabled. */
  isSaving: boolean;
}

export function useUnsavedChangesGuard({
  isDirty,
  onSave,
  onDiscard,
}: UseUnsavedChangesGuardOptions): UseUnsavedChangesGuardReturn {
  const [isSaving, setIsSaving] = useState(false);

  // --- 1. In-app route blocking (RR6 Data Router) ----------------------------
  // `useBlocker` returns a Blocker with `state: "unblocked" | "blocked" | "proceeding"`.
  // We pass a function so the decision is re-evaluated per-navigation against
  // the current `isDirty`, not frozen at first render.
  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: Location;
        nextLocation: Location;
      }) => {
        if (!isDirty) return false;
        // Don't block same-location navigations (e.g. hash changes, refreshes
        // of the same URL from react-router). Only block genuine transitions.
        return currentLocation.pathname !== nextLocation.pathname;
      },
      [isDirty],
    ),
  );

  // --- 2. Tab close / reload / external-URL navigation -----------------------
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required for legacy browsers; the actual text is ignored by modern ones.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // --- 3. Resolution handlers ------------------------------------------------
  const save = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave();
      // Let the blocked navigation proceed. Safe to call even when the
      // blocker is unblocked — it no-ops.
      if (blocker.state === 'blocked') {
        blocker.proceed();
      }
    } finally {
      setIsSaving(false);
    }
  }, [onSave, blocker]);

  const discard = useCallback(() => {
    try {
      onDiscard?.();
    } finally {
      if (blocker.state === 'blocked') {
        blocker.proceed();
      }
    }
  }, [onDiscard, blocker]);

  const cancel = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  return {
    isBlocked: blocker.state === 'blocked',
    save,
    discard,
    cancel,
    isSaving,
  };
}
